/**
 * Reusable sorting utilities for consistent sorting across the application
 */

import type { SortDirection } from '@sharpr/ui';

/**
 * Sort by best EV (Expected Value)
 * Default: Higher EV comes first (descending order)
 */
export function sortByBestEV<T extends { bestMarket?: { ev: number } }>(
  a: T,
  b: T,
  direction: SortDirection = 'desc'
): number {
  const aEv = a.bestMarket?.ev ?? 0;
  const bEv = b.bestMarket?.ev ?? 0;
  const diff = bEv - aEv;
  return direction === 'desc' ? diff : -diff;
}

/**
 * Sort by max EV from best lines
 * Considers both over and under lines
 * Default: Higher EV comes first (descending order)
 */
export function sortByMaxEV<T extends { bestLines?: { over?: { ev?: number }, under?: { ev?: number } } }>(
  a: T,
  b: T,
  direction: SortDirection = 'desc'
): number {
  const aMaxEv = Math.max(
    a.bestLines?.over?.ev ?? 0,
    a.bestLines?.under?.ev ?? 0
  );
  const bMaxEv = Math.max(
    b.bestLines?.over?.ev ?? 0,
    b.bestLines?.under?.ev ?? 0
  );
  const diff = bMaxEv - aMaxEv;
  return direction === 'desc' ? diff : -diff;
}

/**
 * Sort by max EV for a specific sportsbook
 * Filters books array by sportsbook name, then finds max EV
 * Default: Higher EV comes first (descending order)
 * Rows without lines from that sportsbook are pushed to the bottom
 */
export function sortByBookEV<T extends { books: Array<{ book: string, ev?: number }> }>(
  a: T,
  b: T,
  bookName: string,
  direction: SortDirection = 'desc'
): number {
  // Filter to only this sportsbook's lines
  const aBookLines = a.books.filter(line => line.book === bookName);
  const bBookLines = b.books.filter(line => line.book === bookName);

  // Find max EV for this sportsbook (across over/under)
  const aMaxEv = aBookLines.length > 0
    ? Math.max(...aBookLines.map(line => line.ev ?? 0))
    : -Infinity; // Push rows without this book to bottom

  const bMaxEv = bBookLines.length > 0
    ? Math.max(...bBookLines.map(line => line.ev ?? 0))
    : -Infinity;

  const diff = bMaxEv - aMaxEv;
  return direction === 'desc' ? diff : -diff;
}

/**
 * Sort by player name (from primaryInfo.title)
 * Used internally by groupAndSortByPlayer
 */
function sortByPlayerName<T extends { primaryInfo: { title: string } }>(
  a: T,
  b: T,
  direction: SortDirection = 'asc'
): number {
  const diff = a.primaryInfo.title.localeCompare(b.primaryInfo.title);
  return direction === 'desc' ? -diff : diff;
}

/**
 * Sort by calculated EV for finding best lines
 */
export function sortByCalculatedEV<T extends { calculatedEV: number }>(
  a: T,
  b: T
): number {
  return b.calculatedEV - a.calculatedEV; // Higher EV first
}

/**
 * Group and sort rows by player
 * First sorts by player name, then by market within each player
 */
export function groupAndSortByPlayer<T extends {
  primaryInfo: { title: string, tertiary?: string };
  projectionInfo?: { unit: string };
}>(
  rows: T[],
  sortDirection: SortDirection = 'asc'
): T[] {
  return [...rows].sort((a, b) => {
    // First sort by player name
    const playerCompare = sortByPlayerName(a, b, sortDirection);
    if (playerCompare !== 0) return playerCompare;

    // Then by market (unit) within the same player
    const aUnit = a.projectionInfo?.unit || a.primaryInfo.tertiary || '';
    const bUnit = b.projectionInfo?.unit || b.primaryInfo.tertiary || '';
    return aUnit.localeCompare(bUnit);
  });
}